import { buildGeneralTab, buildValidateTab, showWidgetEditDialog } from '@juvare/common-form-ui/WidgetEditDialog';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, renderForm, validateCustom, } from '@juvare/common-form-ui/WidgetForm/Utils';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { orgIdPrefixForAlertService } from '@juvare/contact-management/backend/features/organizations/shared';
import { requireAnyOrgPermission, requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { showOrganizationTreeDialog } from '@juvare/contact-management/ui/features/organizations';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation, useContextClient, useUserState } from '../../../../hooks';
import { type } from './types';
const WidgetSender = (props) => {
    var _a, _b;
    const contextClient = useContextClient();
    const [loading, setLoading] = React.useState(true);
    const { t } = useAlertUiTranslation();
    const [owners, setOwners] = React.useState([]);
    const myselfOwner = owners.find((owner) => owner.externalId.startsWith('account_'));
    const systemOwner = owners.find((owner) => owner.externalId.startsWith('system_'));
    const error = getVisibleError(props);
    const userState = useUserState();
    const externalId = props.getData();
    let selectedOwner;
    const allowSeeMyselfSender = myselfOwner &&
        (requireAnySystemPermission(userState, 'manage-notifications') || requireAnyOrgPermission(userState, 'view-and-message-members'));
    const allowSeeSystemSender = systemOwner && requireAnySystemPermission(userState, 'manage-notifications');
    const allowSeeOrganizationSender = (!loading && requireAnySystemPermission(userState, 'manage-notifications')) ||
        requireAnyOrgPermission(userState, 'view-and-message-members', 'create-notifications');
    React.useEffect(() => {
        contextClient
            .getOwners()
            .then((result) => {
            result.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            setOwners(result);
            setLoading(false);
        })
            .catch(handleErrorSnackbar);
    }, [contextClient]);
    if (externalId && externalId.startsWith('org_')) {
        selectedOwner = owners.find((owner) => owner.externalId === externalId);
    }
    const handleChange = (value) => {
        if (value === 'selectOrganization') {
            showOrganizationTreeDialog({
                includeByOrgPermission: ['view-and-message-members', 'create-notifications'],
                includeVisible: false,
                includeByMembership: false,
                multiSelect: false,
                onChange: (selectedOrganizationIds) => {
                    if (selectedOrganizationIds.length > 0) {
                        const selectedOrganizationOwner = owners.find((owner) => owner.externalId === orgIdPrefixForAlertService + selectedOrganizationIds[0]);
                        if (selectedOrganizationOwner) {
                            props.setData(selectedOrganizationOwner.externalId);
                            renderForm(props, { changed: true });
                        }
                    }
                },
            });
        }
        else {
            props.setData(value);
            renderForm(props, { changed: true });
        }
    };
    if (loading) {
        return React.createElement(CircularProgress, null);
    }
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error },
        React.createElement(InputLabel, null, props.config.label),
        React.createElement(Select, { displayEmpty: true, label: props.config.label, onChange: (event) => handleChange(event.target.value), "data-test": 'sender-select', value: props.getData() || '', disabled: (_b = props.config) === null || _b === void 0 ? void 0 : _b.disabled, sx: { backgroundColor: 'background.paper' }, inputProps: { 'aria-label': props.config.label } },
            allowSeeMyselfSender && React.createElement(MenuItem, { value: myselfOwner.externalId }, t('widget.sender.myself')),
            allowSeeSystemSender && React.createElement(MenuItem, { value: systemOwner.externalId }, systemOwner.name),
            allowSeeOrganizationSender && React.createElement(MenuItem, { value: 'selectOrganization' }, t('widget.sender.selectOrg')),
            selectedOwner && React.createElement(MenuItem, { value: selectedOwner.externalId }, selectedOwner.name)),
        React.createElement(FormHelperErrorDescription, { error: error })));
};
const WidgetSenderEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: props.config.label,
        }, buildGeneralTab(props, { key: true, label: true }), buildValidateTab(props, { required: true })) }),
        React.createElement(WidgetSender, Object.assign({}, props))));
};
export const widgetSender = {
    type,
    createWidget: () => ({
        type,
        label: 'Sender',
        key: 'ownerId',
    }),
    validate: (props) => {
        var _a, _b;
        const value = props.getDataStringOrUndefined();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && !value) {
            return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    component: WidgetSender,
    icon: React.createElement(ContactMailIcon, null),
    editorComponent: WidgetSenderEditor,
};
